import React, { useMemo } from 'react';
import { Target } from 'lucide-react';
import { useFriendMatches } from '../hooks/useFriendMatches';
import { Avatar } from './ui/Avatar';

interface Props {
  userId: string;
  friendIds: string[];
  friendNamesById: Record<string, string>;
  myCounts: Record<string, number>;
}

export function HelpYouCanGive({ friendIds, friendNamesById, myCounts }: Props): React.ReactElement | null {
  const myDuplicateCodes = useMemo(
    () => Object.keys(myCounts).filter((c) => (myCounts[c] || 0) >= 2),
    [myCounts]
  );

  const { matches, loading } = useFriendMatches({
    myDuplicateCodes,
    friendIds,
    friendNamesById,
  });

  if (loading || matches.length === 0) return null;

  const top = matches.slice(0, 3);

  return (
    <section
      className="mx-1 mb-6 bg-white border border-ink-300 rounded-2xl px-4 py-4 shadow-xs"
      aria-label="Você pode ajudar"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="w-7 h-7 rounded-lg bg-gold-100 flex items-center justify-center">
          <Target size={15} className="text-green-900" strokeWidth={2.4} />
        </span>
        <h2 className="font-display font-bold text-[14px] text-green-900 tracking-[-0.01em]">
          Você pode ajudar
        </h2>
      </div>
      <p className="text-caption text-ink-500 mb-3">
        {matches.length} {matches.length === 1 ? 'amigo precisa' : 'amigos precisam'} de figurinhas que você tem repetidas
      </p>
      <ul className="space-y-2">
        {top.map((m) => (
          <li
            key={`${m.friend_id}:${m.sticker_code}`}
            className="flex items-center gap-3 bg-paper border border-ink-300/60 rounded-lg px-3 py-2"
          >
            <Avatar name={m.friend_name} size={32} />
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-[13px] text-ink-900 truncate">
                {m.friend_name.split(/\s+/)[0]} precisa da{' '}
                <span className="font-mono text-green-700">{m.sticker_code}</span>
              </p>
              <p className="text-caption text-ink-500">Prioridade {m.priority}</p>
            </div>
          </li>
        ))}
      </ul>
      {matches.length > 3 && (
        <div className="mt-3 text-center">
          <span className="text-caption font-bold text-green-700">
            Ver todas ({matches.length}) →
          </span>
        </div>
      )}
    </section>
  );
}
